import { AfterViewInit, Component, EventEmitter, OnInit } from '@angular/core';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    DatasourceColumnId,
    datasourceColumnOrderByQuery,
} from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { DatasourceColumnOldDataLoaderFactoryOptions } from '../common/data-loaders/datasource-column-old';
import { SegmentDatasourceColumnDataLoaderFactoryOptions } from '../common/data-loaders/segments';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { FilterCategoryDashboardItemFactoryOptions } from '../common/items/filter/category-filter-dashboard-item/filter-category';
import { ModifyTimeDashboardItemFactoryOptions } from '../common/items/modify-time';
import { ICheckboxSettingDashboardItem } from '../common/items/setting/checkbox/checkbox.component';
import { IToggleSettingDashboardItem } from '../common/items/setting/toggle/toggle.component';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { MopaResourcesChartDocs } from './mopa-resources.chart-documentation';
import { DashboardResourcesByDateDataLoaderFactory } from '../mopa-resources-detailed/data-loaders/resources-by-date';
import { DashboardResourcesByDayNoDataLoaderFactory } from '../mopa-resources-detailed/data-loaders/resources-by-day-no';
import { BiVizDashboardResourcesSqlMap } from '../mopa-resources-detailed/sql-maps/biviz-dashboard-resources-datasource-sql-map';

@Component({
    selector: 'app-mopa-resources',
    templateUrl: './mopa-resources.component.html',
    styleUrls: ['./mopa-resources.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class MopaResourcesComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Resources';
    override DASHBOARD_ID = 'mopa-resources';
    values = Object.values;

    /** GCP Project id used in the dashboard */
    private gcpProjectId: GcpProjectId = currentDataProject().gcpProjectId;

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////

    override filterDataLoaders = {
        'mopa.resources.filter.segment': DataLoaderFactories[
            'common.filter.segment'
        ](
            new SegmentDatasourceColumnDataLoaderFactoryOptions(
                'mopa.resources.filter_segments',
                this.gcpProjectId,
                this.queryCache
            )
        ).create(),
        'mopa.resources.filter.activity_app_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.resources.filter.app_version',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ACTIVITY_APP_VERSION',
            datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery('ACTIVITY_APP_VERSION'),
        }).create(),
        'mopa.resources.filter.activity_app_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.resources.filter.activity_app_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ACTIVITY_APP_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery(
                    'ACTIVITY_APP_VERSION_GROUP_MINOR'
                ),
            }).create(),
        'mopa.resources.filter.daily_total_energy_spent': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.resources.filter.daily_total_energy_spent',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'DAILY_TOTAL_ENERGY_SPENT',
            datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
            filterContext: true,
        }).create(),
        'mopa.resources.filter.firebase_app_store': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.resources.filter.firebase_app_store',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_APP_STORE',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
            })
        ).create(),
        'mopa.resources.filter.firebase_country_tier': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.resources.filter.firebase_country_tier',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
            datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
            // filterContext: ['FIREBASE_COUNTRY'],
        }).create(),
        'mopa.resources.filter.firebase_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.resources.filter.firebase_country',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY',
            datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
            filterContext: ['FIREBASE_COUNTRY_TIER'],
        }).create(),
        'mopa.resources.filter.firebase_country_european_union':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.resources.filter.firebase_country_european_union',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
            }).create(),
        'mopa.resources.filter.attribution_traffic_source': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.resources.filter.attribution_traffic_source',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
            })
        ).create(),
        'mopa.resources.filter.firebase_install_version': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.resources.filter.firebase_install_version',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'FIREBASE_INSTALL_VERSION'
                ),
            })
        ).create(),
        'mopa.resources.filter.firebase_install_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.resources.filter.firebase_install_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                ),
            }).create(),
        'mopa.resources.filter.etl_payer': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.resources.filter.etl_payer',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ETL_PAYER',
            datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
        }).create(),
        'mopa.resources.filter.etl_revenue_badge': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.resources.filter.etl_revenue_badge',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ETL_REVENUE_BADGE',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery('ETL_REVENUE_BADGE'),
            })
        ).create(),
        'mopa.resources.filter.attribution_network_name': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.resources.filter.attribution_network_name',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
            })
        ).create(),
        'mopa.resources.filter.attribution_campaign_name': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.resources.filter.attribution_campaign_name',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                filterContext: true,
            })
        ).create(),
        'mopa.resources.filter.resources_source': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.resources.filter.resources_source',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'RESOURCES_SOURCE',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                filterContext: true,
            })
        ).create(),
        'mopa.resources.filter.resource_name': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.resources.filter.resource_name',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'RESOURCE_NAME',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                filterContext: true,
            })
        ).create(),
        'mopa.resources.filter.resource_payment_type': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.resources.filter.resource_payment_type',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'RESOURCE_PAYMENT_TYPE',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'RESOURCE_PAYMENT_TYPE'
                ),
            })
        ).create(),
        'mopa.resources.filter.resource_source_name': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.resources.filter.resource_source_name',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'RESOURCE_SOURCE_NAME',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                filterContext: true,
            })
        ).create(),
        'mopa.resources.filter.resource_source_category': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId:
                    'mopa.resources.filter.resource_source_category',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'RESOURCE_SOURCE_CATEGORY',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                filterContext: true,
            })
        ).create(),
        'mopa.resources.filter.change_type': DataLoaderFactories[
            'common.filter.datasource_column_old'
        ](
            new DatasourceColumnOldDataLoaderFactoryOptions({
                createdInstanceId: 'mopa.resources.filter.change_type',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'CHANGE_TYPE',
                datasourceSqlMap: BiVizDashboardResourcesSqlMap.sqlMap,
                filterContext: true,
            })
        ).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    CHART DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    override chartDataLoaders = {
        'mopa.resources.modify_time_users_datasource': DataLoaderFactories[
            'common.modify_time'
        ]({
            createdInstanceId: 'mopa.resources.modify_time_users_datasource',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasetId: BiVizDashboardResourcesSqlMap.sqlMap.dataset,
            tableId: BiVizDashboardResourcesSqlMap.sqlMap.tableId,
        }).create(),
        'mopa.resources.resources_by_date':
            new DashboardResourcesByDateDataLoaderFactory({
                createdInstanceId: 'mopa.resources.resources_by_date',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),
        'mopa.resources.resources_by_day_no':
            new DashboardResourcesByDayNoDataLoaderFactory({
                createdInstanceId: 'mopa.resources.resources_by_day_no',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////    SETTINGS
    /////////////////////////////////////////////////////////////////

    /** Toggle between active_day / install_day for "by day_no" charts. */
    settingDayMode: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_DAY_MODE,
            viewtype: 'toggle',
            displayName: 'Day mode',
            description: 'Affects "by day_no" charts',
            value: 'install_day',
            selectOptions: [
                'install_day',
                // 'active_day'
            ],
        },
    };

    settingShowAllUsers: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_SHOW_ALL_USERS,
            viewtype: 'checkbox',
            displayName: 'All',
            value: false,
        },
    };

    settingShowMetaUsers: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_SHOW_META_USERS,
            viewtype: 'checkbox',
            displayName: 'META',
            value: true,
        },
    };
    settingShowHopaUsers: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_SHOW_HOPA_USERS,
            viewtype: 'checkbox',
            displayName: 'HOPA',
            value: true,
        },
    };
    settingShowMetaProductiveUsers: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_SHOW_META_PRODUCTIVE_USERS,
            viewtype: 'checkbox',
            displayName: 'META Prod.',
            value: true,
        },
    };
    settingShowCoreLoopUsers: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.META_SHOW_CORE_LOOP_USERS,
            viewtype: 'checkbox',
            displayName: 'CORE LOOP',
            value: true,
        },
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////

    // CHARTS
    modify_time_users_datasource: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.modify_time_users_datasource',
        dashboardItemFactory: DashboardItemFactories['common.modify_time'](
            new ModifyTimeDashboardItemFactoryOptions(
                'mopa.resources.modify_time_users_datasource',
                'value'
            )
        ),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.modify_time_users_datasource']
                .emitter,
    };

    energy_inflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.energy_inflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources.chart.energy_inflow',
            dashboardItem: {
                title: 'Energy inflow',
                chartDocumentation: MopaResourcesChartDocs.energy_inflow,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'energy_inflow',
            metricName: 'Energy inflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    avg_energy_inflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.energy_inflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources.chart.energy_inflow_per_active_user',
            dashboardItem: {
                title: 'Avg. Energy inflow per active user',
                chartDocumentation:
                    MopaResourcesChartDocs.avg_energy_inflow_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_energy_inflow_per_active_user',
            metricName: 'Avg. Energy inflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    running_avg_energy_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_energy_inflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources.chart.running_avg_energy_inflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Energy inflow per user by day no',
                    chartDocumentation:
                        MopaResourcesChartDocs.running_avg_energy_inflow_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_energy_inflow_per_user',
                metricName: 'Running avg. Energy inflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.resources.resources_by_day_no']
                    .emitter,
        };

    energy_outflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.energy_outflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources.chart.energy_outflow',
            dashboardItem: {
                title: 'Energy outflow',
                chartDocumentation: MopaResourcesChartDocs.energy_outflow,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'energy_outflow',
            metricName: 'Energy outflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    avg_energy_outflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_energy_outflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources.chart.avg_energy_outflow_per_active_user',
            dashboardItem: {
                title: 'Avg. Energy outflow per active user',
                chartDocumentation:
                    MopaResourcesChartDocs.avg_energy_outflow_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_energy_outflow_per_active_user',
            metricName: 'Avg. Energy outflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    running_avg_energy_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_energy_outflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources.chart.running_avg_energy_outflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Energy outflow per user by day no',
                    chartDocumentation:
                        MopaResourcesChartDocs.running_avg_energy_outflow_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_energy_outflow_per_user',
                metricName: 'Running avg. Energy outflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.resources.resources_by_day_no']
                    .emitter,
        };

    energy_balance: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.energy_inflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources.chart.energy_balance',
            dashboardItem: {
                title: 'Energy balance',
                chartDocumentation: MopaResourcesChartDocs.energy_balance,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'energy_balance',
            metricName: 'Energy balace',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    avg_energy_balance_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_energy_balance_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources.chart.avg_energy_balance_per_active_user',
            dashboardItem: {
                title: 'Avg. Energy balance per active user',
                chartDocumentation:
                    MopaResourcesChartDocs.avg_energy_balance_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_energy_balance_per_active_user',
            metricName: 'Avg. Energy balance per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    running_avg_energy_balance_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_energy_balance_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources.chart.running_avg_energy_balance_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Energy balance per user by day no',
                    chartDocumentation:
                        MopaResourcesChartDocs.running_avg_energy_balance_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_energy_balance_per_user',
                metricName: 'Running avg. Energy balance per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.resources.resources_by_day_no']
                    .emitter,
        };

    gems_inflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.gems_inflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources.chart.gems_inflow',
            dashboardItem: {
                title: 'Gems inflow',
                chartDocumentation: MopaResourcesChartDocs.gems_inflow,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'gems_inflow',
            metricName: 'Gems inflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    avg_gems_inflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_gems_inflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources.chart.avg_gems_inflow_per_active_user',
            dashboardItem: {
                title: 'Avg. Gems inflow per active user',
                chartDocumentation:
                    MopaResourcesChartDocs.avg_gems_inflow_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_gems_inflow_per_active_user',
            metricName: 'Avg. Gems inflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    running_avg_gems_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.running_avg_gems_inflow_per_user_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources.chart.running_avg_gems_inflow_per_user_by_day_no',
            dashboardItem: {
                title: 'Running avg. Gems inflow per user by day no',
                chartDocumentation:
                    MopaResourcesChartDocs.running_avg_gems_inflow_per_user_by_day_no,
            },
            xColumnLabel: 'day_no',
            xAxisType: 'value',
            yColumnLabel: 'running_avg_gems_inflow_per_user',
            metricName: 'Running avg. Gems inflow per user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) => `Day no: ${label}`;
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_day_no'].emitter,
    };

    gems_outflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.gems_outflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources.chart.gems_outflow',
            dashboardItem: {
                title: 'Gems outflow',
                chartDocumentation: MopaResourcesChartDocs.gems_outflow,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'gems_outflow',
            metricName: 'Gems outflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    avg_gems_outflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_gems_outflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources.chart.avg_gems_outflow_per_active_user',
            dashboardItem: {
                title: 'Avg. Gems outflow per active user',
                chartDocumentation:
                    MopaResourcesChartDocs.avg_gems_outflow_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_gems_outflow_per_active_user',
            metricName: 'Avg. Gems outflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    running_avg_gems_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_gems_outflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources.chart.running_avg_gems_outflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Gems outflow per user by day no',
                    chartDocumentation:
                        MopaResourcesChartDocs.running_avg_gems_outflow_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_gems_outflow_per_user',
                metricName: 'Running avg. Gems outflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.resources.resources_by_day_no']
                    .emitter,
        };

    gems_balance: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.gems_balance',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources.chart.gems_balance',
            dashboardItem: {
                title: 'Gems balance',
                chartDocumentation: MopaResourcesChartDocs.gems_balance,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'gems_balance',
            metricName: 'Gems balance',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    avg_gems_balance_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_gems_balance_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources.chart.avg_gems_balance_per_active_user',
            dashboardItem: {
                title: 'Avg. Gems balance per active user',
                chartDocumentation:
                    MopaResourcesChartDocs.avg_gems_balance_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_gems_balance_per_active_user',
            metricName: 'Avg. Gems balance per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    running_avg_gems_balance_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_gems_balance_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources.chart.running_avg_gems_balance_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Gems balance per user by day no',
                    chartDocumentation:
                        MopaResourcesChartDocs.running_avg_gems_balance_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_gems_balance_per_user',
                metricName: 'Running avg. Gems balance per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.resources.resources_by_day_no']
                    .emitter,
        };

    hints_inflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.hints_inflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources.chart.hints_inflow',
            dashboardItem: {
                title: 'Hints inflow',
                chartDocumentation: MopaResourcesChartDocs.hints_inflow,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'hints_inflow',
            metricName: 'Hints inflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    avg_hints_inflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_hints_inflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources.chart.avg_hints_inflow_per_active_user',
            dashboardItem: {
                title: 'Avg. Hints inflow per active user',
                chartDocumentation:
                    MopaResourcesChartDocs.avg_hints_inflow_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_hints_inflow_per_active_user',
            metricName: 'Avg. Hints inflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    running_avg_hints_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_hints_inflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources.chart.running_avg_hints_inflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Hints inflow per user by day no',
                    chartDocumentation:
                        MopaResourcesChartDocs.running_avg_hints_inflow_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_hints_inflow_per_user',
                metricName: 'Running avg. Hints inflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.resources.resources_by_day_no']
                    .emitter,
        };

    hints_outflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.hints_outflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources.chart.hints_outflow',
            dashboardItem: {
                title: 'Hints outflow',
                chartDocumentation: MopaResourcesChartDocs.hints_outflow,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'hints_outflow',
            metricName: 'Hints outflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    avg_hints_outflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_hints_outflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources.chart.avg_hints_outflow_per_active_user',
            dashboardItem: {
                title: 'Avg. Hints outflow per active user',
                chartDocumentation:
                    MopaResourcesChartDocs.avg_hints_outflow_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_hints_outflow_per_active_user',
            metricName: 'Avg. Hints outflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    running_avg_hints_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_hints_outflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources.chart.running_avg_hints_outflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Hints outflow per user by day no',
                    chartDocumentation:
                        MopaResourcesChartDocs.running_avg_hints_outflow_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_hints_outflow_per_user',
                metricName: 'Running avg. Hints outflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.resources.resources_by_day_no']
                    .emitter,
        };

    hints_balance: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.hints_balance',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources.chart.hints_balance',
            dashboardItem: {
                title: 'Hints balance',
                chartDocumentation: MopaResourcesChartDocs.hints_balance,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'hints_balance',
            metricName: 'Hints balance',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    avg_hints_balance_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_hints_balance_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources.chart.avg_hints_balance_per_active_user',
            dashboardItem: {
                title: 'Avg. Hints balance per active user',
                chartDocumentation:
                    MopaResourcesChartDocs.avg_hints_balance_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_hints_balance_per_active_user',
            metricName: 'Avg. Hints balance per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    running_avg_hints_balance_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_hints_balance_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources.chart.running_avg_hints_balance_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Hints balance per user by day no',
                    chartDocumentation:
                        MopaResourcesChartDocs.running_avg_hints_balance_per_user_by_day_no,
                },
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_hints_balance_per_user',
                metricName: 'Running avg. Hints balance per user',
                echartsSeriesType: 'line',
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.resources.resources_by_day_no']
                    .emitter,
        };

    coins_inflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.coins_inflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources.chart.coins_inflow',
            dashboardItem: {
                title: 'Coins inflow',
                chartDocumentation: MopaResourcesChartDocs.coins_inflow,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'coins_inflow',
            metricName: 'Coinsinflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    avg_coins_inflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_coins_inflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources.chart.avg_coins_inflow_per_active_user',
            dashboardItem: {
                title: 'Avg. Coins inflow per active user',
                chartDocumentation:
                    MopaResourcesChartDocs.avg_coins_inflow_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_coins_inflow_per_active_user',
            metricName: 'Avg. Coins inflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    running_avg_coins_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_coins_inflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources.chart.running_avg_coins_inflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Coins inflow per user by day no',
                    chartDocumentation:
                        MopaResourcesChartDocs.running_avg_coins_inflow_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_coins_inflow_per_user',
                metricName: 'Running avg. Coins inflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.resources.resources_by_day_no']
                    .emitter,
        };

    coins_outflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.coins_outflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources.chart.coins_outflow',
            dashboardItem: {
                title: 'Coins outflow',
                chartDocumentation: MopaResourcesChartDocs.coins_outflow,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'coins_outflow',
            metricName: 'Coins outflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    avg_coins_outflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_coins_outflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources.chart.avg_coins_outflow_per_active_user',
            dashboardItem: {
                title: 'Avg. Coins outflow per active user',
                chartDocumentation:
                    MopaResourcesChartDocs.avg_coins_outflow_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_coins_outflow_per_active_user',
            metricName: 'Avg. Coins outflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    running_avg_coins_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_coins_outflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources.chart.running_avg_coins_outflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Coins outflow per user by day no',
                    chartDocumentation:
                        MopaResourcesChartDocs.running_avg_coins_outflow_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_coins_outflow_per_user',
                metricName: 'Running avg. Coins outflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.resources.resources_by_day_no']
                    .emitter,
        };

    coins_balance: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.coins_balance',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources.chart.coins_balance',
            dashboardItem: {
                title: 'Coins balance',
                chartDocumentation: MopaResourcesChartDocs.coins_balance,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'coins_balance',
            metricName: 'Coins balance',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    avg_coins_balance_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_coins_balance_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources.chart.avg_coins_balance_per_active_user',
            dashboardItem: {
                title: 'Avg. Coins balance per active user',
                chartDocumentation:
                    MopaResourcesChartDocs.avg_coins_balance_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_coins_balance_per_active_user',
            metricName: 'Avg. Coins balance per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    running_avg_coins_balance_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_coins_balance_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources.chart.running_avg_coins_balance_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Coins balance per user by day no',
                    chartDocumentation:
                        MopaResourcesChartDocs.running_avg_coins_balance_per_user_by_day_no,
                },
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_coins_balance_per_user',
                metricName: 'Running avg. Coins balance per user',
                echartsSeriesType: 'line',
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.resources.resources_by_day_no']
                    .emitter,
        };

    compasses_inflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.compasses_inflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources.chart.compasses_inflow',
            dashboardItem: {
                title: 'Compasses inflow',
                chartDocumentation: MopaResourcesChartDocs.compasses_inflow,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'compasses_inflow',
            metricName: 'Compasses inflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    avg_compasses_inflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_compasses_inflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources.chart.avg_compasses_inflow_per_active_user',
            dashboardItem: {
                title: 'Avg. Compasses inflow per active user',
                chartDocumentation:
                    MopaResourcesChartDocs.avg_compasses_inflow_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_compasses_inflow_per_active_user',
            metricName: 'Avg. Compasses inflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    running_avg_compasses_inflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_compasses_inflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources.chart.running_avg_compasses_inflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Compasses inflow per user by day no',
                    chartDocumentation:
                        MopaResourcesChartDocs.running_avg_compasses_inflow_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_compasses_inflow_per_user',
                metricName: 'Running avg. Compasses inflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.resources.resources_by_day_no']
                    .emitter,
        };

    compasses_outflow: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.compasses_outflow',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources.chart.compasses_outflow',
            dashboardItem: {
                title: 'Compasses outflow',
                chartDocumentation: MopaResourcesChartDocs.compasses_outflow,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'compasses_outflow',
            metricName: 'Compasses outflow',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    avg_compasses_outflow_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_compasses_outflow_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources.chart.avg_compasses_outflow_per_active_user',
            dashboardItem: {
                title: 'Avg. Compasses outflow per active user',
                chartDocumentation:
                    MopaResourcesChartDocs.avg_compasses_outflow_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_compasses_outflow_per_active_user',
            metricName: 'Avg. Compasses outflow per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    running_avg_compasses_outflow_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_compasses_outflow_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources.chart.running_avg_compasses_outflow_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Compasses outflow per user by day no',
                    chartDocumentation:
                        MopaResourcesChartDocs.running_avg_compasses_outflow_per_user_by_day_no,
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_compasses_outflow_per_user',
                metricName: 'Running avg. Compasses outflow per user',
                echartsSeriesType: 'line',
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.resources.resources_by_day_no']
                    .emitter,
        };

    compasses_balance: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.compasses_balance',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.resources.chart.compasses_balance',
            dashboardItem: {
                title: 'Compasses balance',
                chartDocumentation: MopaResourcesChartDocs.compasses_balance,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'compasses_balance',
            metricName: 'Compasses balance',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipIntegerValueFormatter(
                            value
                        );
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    avg_compasses_balance_per_active_user: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.resources.chart.avg_compasses_balance_per_active_user',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.resources.chart.avg_compasses_balance_per_active_user',
            dashboardItem: {
                title: 'Avg. Compasses balance per active user',
                chartDocumentation:
                    MopaResourcesChartDocs.avg_compasses_balance_per_active_user,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_compasses_balance_per_active_user',
            metricName: 'Avg. Compasses balance per active user',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            yAxisLabel: {
                formatter: EChartsAxisHelper.axisNumberLabelFormatter,
            },
            tooltip: {
                xLabelFormatterGetter(context) {
                    return (label: any) =>
                        EChartsAxisHelper.axisLabelDateFormatter(
                            label,
                            context
                        );
                },
                valueFormatterGetter(context) {
                    return (value: any) =>
                        EChartsTooltipHelper.tooltipNumberValueFormatter(value);
                },
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.resources.resources_by_date'].emitter,
    };

    running_avg_compasses_balance_per_user_by_day_no: IDashboardItemDataLoaderLink =
        {
            id: 'lnk.mopa.resources.chart.running_avg_compasses_balance_per_user_by_day_no',
            dashboardItemFactory: DashboardItemFactories[
                'common.chart.xy_chart'
            ]({
                dashboardName: this.DASHBOARD_NAME,
                createdInstanceId:
                    'mopa.resources.chart.running_avg_compasses_balance_per_user_by_day_no',
                dashboardItem: {
                    title: 'Running avg. Compasses balance per user by day no',
                    chartDocumentation:
                        MopaResourcesChartDocs.running_avg_compasses_balance_per_user_by_day_no,
                },
                colorGetterBuilder: (context: IQueryContext) => {
                    // get all split columns ids
                    const splitColumnsIds = context.datasourceColumns
                        .filter((dc) => dc.state.isSplitApplied)
                        .map((dc) => dc.id);
                    // create layer from spliColumns
                    const layer =
                        ChartColorService.getLayerString(splitColumnsIds);
                    return (s: string) => ({
                        color: this.colorService.getColor(layer, s),
                    });
                },
                xColumnLabel: 'day_no',
                xAxisType: 'value',
                yColumnLabel: 'running_avg_compasses_balance_per_user',
                metricName: 'Running avg. Compasses balance per user',
                echartsSeriesType: 'line',
                yAxisLabel: {
                    formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                },
                tooltip: {
                    xLabelFormatterGetter(context) {
                        return (label: any) => `Day no: ${label}`;
                    },
                    valueFormatterGetter(context) {
                        return (value: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                value
                            );
                    },
                },
            }),
            dataLoaderEmitter:
                this.chartDataLoaders['mopa.resources.resources_by_day_no']
                    .emitter,
        };

    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        RESOURCE_NAME: {
            id: 'mopa.resources.filter.resource_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.resource_name',
                    'RESOURCE_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.resources.filter.resource_name']
                    .emitter,
        },
        CHANGE_TYPE: {
            id: 'mopa.resources.filter.change_type',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.change_type',
                    'CHANGE_TYPE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.resources.filter.change_type']
                    .emitter,
        },
        RESOURCE_PAYMENT_TYPE: {
            id: 'mopa.resources.filter.resource_payment_type',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.resource_payment_type',
                    'RESOURCE_PAYMENT_TYPE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources.filter.resource_payment_type'
                ].emitter,
        },
        RESOURCE_SOURCE_CATEGORY: {
            id: 'mopa.resources.filter.resource_source_category',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.resource_source_category',
                    'RESOURCE_SOURCE_CATEGORY'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources.filter.resource_source_category'
                ].emitter,
        },
        RESOURCE_SOURCE_NAME: {
            id: 'mopa.resources.filter.resource_source_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.resource_source_name',
                    'RESOURCE_SOURCE_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources.filter.resource_source_name'
                ].emitter,
        },
        RESOURCES_SOURCE: {
            id: 'mopa.resources.filter.resources_source',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.resources_source',
                    'RESOURCES_SOURCE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.resources.filter.resources_source']
                    .emitter,
        },
        ACTIVITY_DATE: {
            id: 'mopa.resources.filter.activity_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId: 'mopa.resources.filter.activity_date',
                datasourceColumnId: 'ACTIVITY_DATE',
                splitable: false,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        ACTIVITY_APP_VERSION_GROUP_MINOR: {
            id: 'mopa.resources.filter.app_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.app_version_group_minor',
                    'ACTIVITY_APP_VERSION_GROUP_MINOR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources.filter.activity_app_version_group_minor'
                ].emitter,
        },
        ACTIVITY_APP_VERSION: {
            id: 'mopa.resources.filter.app_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.app_version',
                    'ACTIVITY_APP_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources.filter.activity_app_version'
                ].emitter,
        },
        // DAILY_TOTAL_ENERGY_SPENT: {
        //     id: 'mopa.resources.filter.daily_total_energy_spent',
        //     dashboardItemFactory: DashboardItemFactories['filter.category']({
        //         createdInstanceId:
        //             'mopa.resources.filter.daily_total_energy_spent',
        //         datasourceColumnId: 'DAILY_TOTAL_ENERGY_SPENT',
        //     }),
        //     dataLoaderEmitter:
        //         this.filterDataLoaders[
        //             'mopa.resources.filter.daily_total_energy_spent'
        //         ].emitter,
        // },
        SEGMENT: {
            id: 'mopa.resources.filter.segments',
            dashboardItemFactory: DashboardItemFactories['filter.segments']({
                createdInstanceId: 'mopa.resources.filter.segments',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.resources.filter.segment'].emitter,
        },
        FIREBASE_FIRST_OPEN_DATE: {
            id: 'mopa.resources.filter.firebase_first_open_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId:
                    'mopa.resources.filter.firebase_first_open_date',
                datasourceColumnId: 'FIREBASE_FIRST_OPEN_DATE',
                splitable: true,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        FIREBASE_APP_STORE: {
            id: 'mopa.resources.filter.firebase_app_store',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.firebase_app_store',
                    'FIREBASE_APP_STORE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources.filter.firebase_app_store'
                ].emitter,
        },
        FIREBASE_COUNTRY_EUROPEAN_UNION: {
            id: 'mopa.resources.filter.firebase_country_european_union',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.firebase_country_european_union',
                    'FIREBASE_COUNTRY_EUROPEAN_UNION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources.filter.firebase_country_european_union'
                ].emitter,
        },
        FIREBASE_COUNTRY_TIER: {
            id: 'mopa.resources.filter.firebase_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.firebase_country_tier',
                    'FIREBASE_COUNTRY_TIER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources.filter.firebase_country_tier'
                ].emitter,
        },
        FIREBASE_COUNTRY: {
            id: 'mopa.resources.filter.firebase_country',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.firebase_country',
                    'FIREBASE_COUNTRY'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.resources.filter.firebase_country']
                    .emitter,
        },
        ATTRIBUTION_TRAFFIC_SOURCE: {
            id: 'mopa.resources.filter.attribution_traffic_source',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.attribution_traffic_source',
                    'ATTRIBUTION_TRAFFIC_SOURCE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources.filter.attribution_traffic_source'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION_GROUP_MINOR: {
            id: 'mopa.resources.filter.firebase_install_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.firebase_install_version_group_minor',
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources.filter.firebase_install_version_group_minor'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION: {
            id: 'mopa.resources.filter.firebase_install_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.firebase_install_version',
                    'FIREBASE_INSTALL_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources.filter.firebase_install_version'
                ].emitter,
        },
        ETL_PAYER: {
            id: 'mopa.resources.filter.etl_payer',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.etl_payer',
                    'ETL_PAYER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.resources.filter.etl_payer']
                    .emitter,
        },
        ETL_REVENUE_BADGE: {
            id: 'mopa.resources.filter.etl_revenue_badge',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.etl_revenue_badge',
                    'ETL_REVENUE_BADGE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources.filter.etl_revenue_badge'
                ].emitter,
        },
        ATTRIBUTION_NETWORK_NAME: {
            id: 'mopa.resources.filter.attribution_network_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.attribution_network_name',
                    'ATTRIBUTION_NETWORK_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources.filter.attribution_network_name'
                ].emitter,
        },
        ATTRIBUTION_CAMPAIGN_NAME: {
            id: 'mopa.resources.filter.attribution_campaign_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.resources.filter.attribution_campaign_name',
                    'ATTRIBUTION_CAMPAIGN_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.resources.filter.attribution_campaign_name'
                ].emitter,
        },
    };

    override async ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'MopaResourcesComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }

    async ngAfterViewInit() {
        console.log({
            msg: 'MopaResourcesComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const firebaseAppStoreIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_APP_STORE',
                state: {
                    value: {
                        options: [
                            'Amazon Appstore',
                            'Google Play',
                            'Microsoft Store',
                            'iTunes',
                            'iTunes macOS',
                        ],
                        filterType: 'include',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const firebaseCountryTierIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_COUNTRY_TIER',
                state: {
                    value: {
                        options: ['Tier 1', 'Tier 2', 'Tier 3'],
                        filterType: 'include',
                    },
                    // filterType: 'include',
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'ACTIVITY_DATE',
                state: {
                    value: {
                        preset: 'last 180 days',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const FirebaseFirstOpenDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_FIRST_OPEN_DATE',
                state: {
                    value: {
                        preset: 'last 180 days',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [
            // firebaseAppStoreIC,
            // firebaseCountryTierIC,
            activityDateIC,
            FirebaseFirstOpenDateIC,
        ];

        return initialContext;
    }
}
